import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError, apiFetch } from '../../lib/api'
import { decodeToken } from '../../lib/auth'
import { useAuthSession } from '../../lib/authSession'
import type { DocumentRef } from '../../lib/documentReader'
import { formatNumberString, formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import { capabilities, catalogue, errorText, preview as fetchPreview, readEdit } from './api'
import {
  fieldSchema,
  inputKind,
  makePatch,
  matchesTarget,
  resultLabel,
  valueText,
  scopeKey,
  type Definition,
  type EditResult,
  type EditScope,
  type EditTarget,
  type Lifecycle,
  type Preview,
  type UnchangedResult,
} from './contract'
import InstrumentDateResult from './InstrumentDateResult'
import { dateResultView, sameDateRequest, type InstrumentDateRequest } from './datePlan'
import './recordEdits.css'

interface Props {
  target: EditTarget
  scope: EditScope
  documents?: DocumentRef[]
  initialEditId?: string | null
  legacyFallback?: ReactNode
  onResult?: (result: EditResult) => void
  onNewCorrection?: () => void
  onRecordChanged?: () => void
  onBusyChange?: (busy: boolean) => void
}
export default function RecordEditForm(props: Props) {
  const token = useAuthSession()
  const requestedId = props.initialEditId ?? null
  const [reportedId, setReportedId] = useState(requestedId)
  const [selection, setSelection] = useState({ requestedId, generation: 0 })
  // A changed bookmark/review selection must replace the old controller before
  // it can act. Its own saved ID and New correction callback are continuations:
  // remounting those would discard an exact second-click warning/request.
  if (selection.requestedId !== requestedId) {
    const external = requestedId !== reportedId
    setSelection({ requestedId, generation: selection.generation + (external ? 1 : 0) })
    if (external) setReportedId(requestedId)
  }
  return (
    <Editor
      key={`${token}:${props.target.kind}:${props.target.id}:${scopeKey(props.scope)}:${selection.generation}`}
      {...props}
      onResult={(result) => {
        setReportedId(result.id)
        props.onResult?.(result)
      }}
      onNewCorrection={() => {
        setReportedId(null)
        props.onNewCorrection?.()
      }}
      token={token}
    />
  )
}
function Editor({
  target,
  scope,
  documents = [],
  initialEditId,
  legacyFallback,
  onResult,
  onRecordChanged,
  onBusyChange,
  onNewCorrection,
  token,
}: Props & { token: string | null }) {
  const [fields, setFields] = useState<Definition[]>([])
  const [preview, setPreview] = useState<Preview | null>(null)
  const [dateMode, setDateMode] = useState(false)
  const [dateReference, setDateReference] = useState('')
  const [dateAsOf, setDateAsOf] = useState('')
  const [datePreview, setDatePreview] = useState<Preview | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [explanation, setExplanation] = useState('')
  const [result, setResult] = useState<EditResult | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [lifecycle, setLifecycle] = useState<Lifecycle | null>(null)
  const [editing, setEditing] = useState(true)
  const [busy, setBusy] = useState(true)
  useEffect(() => {
    onBusyChange?.(busy)
    return () => onBusyChange?.(false)
  }, [busy, onBusyChange])
  const [dark, setDark] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [decisionWarning, setDecisionWarning] = useState<'approve' | 'reject' | null>(null)
  const intent = useRef(crypto.randomUUID())
  const pendingRequest = useRef<{ path: string; body: Record<string, unknown> } | null>(null)
  const mounted = useRef(true)
  const role = token ? decodeToken(token)?.role : null
  const manager = (role === 'manager' || role === 'admin') && !permissionDenied
  useEffect(() => {
    onBusyChange?.(busy)
    return () => onBusyChange?.(false)
  }, [busy, onBusyChange])

  function checked<T extends Preview | EditResult | UnchangedResult>(value: T): T {
    if (!matchesTarget(value, target, scope))
      throw new Error('The response belongs to another Record context. Reopen this object.')
    return value
  }
  function received(value: EditResult) {
    if (!mounted.current) return
    checked(value)
    setResult(value)
    setSelected(new Set())
    onResult?.(value)
  }
  async function fresh(keepDraft: boolean) {
    const value = checked(await fetchPreview(target, scope))
    if (!mounted.current) return value
    setPreview(value)
    setDatePreview(null)
    if (!keepDraft) {
      setDateMode(false)
      setDateReference('')
      setDateAsOf('')
      setDraft(
        Object.fromEntries(
          Object.entries(value.values).map(([name, current]) => [name, valueText(current)])
        )
      )
      setTouched(new Set())
    }
    return value
  }
  useEffect(() => {
    let stopped = false
    mounted.current = true
    void (async () => {
      try {
        const flags = await capabilities()
        if (stopped || !mounted.current) return
        if (flags.record_edits_enabled === false) {
          setDark(true)
          return
        }
        if (flags.record_edits_enabled !== true)
          throw new Error(
            'Record edit availability could not be established. Refresh before editing.'
          )
        const metadata = await catalogue(target.kind)
        if (stopped || !mounted.current) return
        setFields(
          metadata.fields
            .filter((field) => field.editable && field.correction)
            .sort((a, b) => a.order - b.order)
        )
        if (initialEditId) {
          const old = checked(await readEdit(initialEditId))
          if (stopped || !mounted.current) return
          received(old)
          setEditing(false)
        } else {
          await fresh(false)
        }
      } catch (failure) {
        if (!stopped && mounted.current) setError(errorText(failure))
      } finally {
        if (!stopped && mounted.current) setBusy(false)
      }
    })()
    return () => {
      stopped = true
      mounted.current = false
    }
    // Identity/auth changes remount Editor; callback identity must not reset drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function change(field: string, value: string) {
    setDatePreview(null)
    if (field === 'balance_as_of' && !lifecycle && !dateMode && target.kind === 'instrument') {
      const current = preview?.date_references?.filter((reference) => reference.is_current) ?? []
      if (
        current.length === 1 &&
        value !== valueText(preview?.values.balance_as_of) &&
        value !== valueText(current[0].financial_date)
      ) {
        setDateMode(true)
        setDateReference(current[0].id)
      }
    }
    setDraft((current) => ({ ...current, [field]: value }))
    setTouched((current) => new Set([...current, field]))
    pendingRequest.current = null
    intent.current = crypto.randomUUID()
    if (result?.status === 'awaiting_ack') setResult(null)
    setNotice(null)
  }
  function changeExplanation(value: string) {
    setExplanation(value)
    setDecisionWarning(null)
    pendingRequest.current = null
    intent.current = crypto.randomUUID()
    if (result?.status === 'awaiting_ack') setResult(null)
  }
  async function failure(error: unknown) {
    if (!mounted.current) return
    setError(errorText(error))
    if (error instanceof ApiError && [403, 404].includes(error.status)) {
      setPermissionDenied(true)
      if (result) {
        try {
          const current = checked(await readEdit(result.id))
          if (mounted.current) received(current)
        } catch {
          /* Preserve the denied decision; never claim it applied. */
        }
      }
    }
    if (error instanceof ApiError && error.status === 409) {
      pendingRequest.current = null
      setDecisionWarning(null)
      intent.current = crypto.randomUUID()
      try {
        await fresh(true)
        if (result && mounted.current) received(checked(await readEdit(result.id)))
        if (mounted.current)
          setNotice(
            'The current Record has been refreshed. Your entries are retained; review them before submitting again.'
          )
      } catch (refreshError) {
        if (mounted.current) {
          setPreview(null)
          setError(errorText(refreshError))
        }
      }
    }
  }
  const allowed = lifecycle
    ? new Set([
        ...lifecycle.edit.fields
          .filter((field) => lifecycle.fieldIds.includes(field.id))
          .map((field) => field.field),
        ...(lifecycle.kind === 'amend' ? lifecycle.edit.required_fields : []),
      ])
    : null
  const shown = fields.filter((field) => !allowed || allowed.has(field.key))
  const required = lifecycle?.kind === 'amend' ? lifecycle.edit.required_fields : []

  const dateReferences = preview?.date_references ?? []
  const dateRequest: InstrumentDateRequest | null =
    dateMode && dateReference && dateAsOf
      ? {
          selected_reference_id: dateReference,
          replacement_date: draft.balance_as_of || null,
          as_of: dateAsOf,
        }
      : null
  const reviewedDatePlan =
    datePreview?.date_plan &&
    dateRequest &&
    sameDateRequest(datePreview.date_plan.request, dateRequest)
      ? datePreview.date_plan
      : null
  const otherDateChanges =
    dateMode &&
    shown.some(
      (field) =>
        field.key !== 'balance_as_of' &&
        touched.has(field.key) &&
        (draft[field.key] ?? '') !== valueText(preview?.values[field.key])
    )

  function changeDateSelection(referenceId: string, asOf: string) {
    setDateReference(referenceId)
    setDateAsOf(asOf)
    setDatePreview(null)
    pendingRequest.current = null
    intent.current = crypto.randomUUID()
    setNotice(null)
    if (result?.status === 'awaiting_ack') setResult(null)
  }
  function openDateCorrection() {
    const current = dateReferences.filter((reference) => reference.is_current)
    setDateMode(true)
    changeDateSelection(current.length === 1 ? current[0].id : '', '')
  }
  async function cancelDateCorrection() {
    if (busy || permissionDenied) return
    setBusy(true)
    setError(null)
    try {
      // A date-plan preview token cannot authorize a different ordinary patch.
      const value = await fresh(true)
      if (!mounted.current) return
      setDateMode(false)
      changeDateSelection('', '')
      setDraft((current) => ({ ...current, balance_as_of: valueText(value.values.balance_as_of) }))
      setTouched((current) => {
        const next = new Set(current)
        next.delete('balance_as_of')
        return next
      })
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  async function reviewDateResult() {
    if (!dateRequest || busy || permissionDenied || otherDateChanges) return
    setBusy(true)
    setError(null)
    setDatePreview(null)
    pendingRequest.current = null
    intent.current = crypto.randomUUID()
    try {
      const value = checked(await fetchPreview(target, scope, dateRequest))
      if (!value.date_plan || !sameDateRequest(value.date_plan.request, dateRequest))
        throw new Error('The balance date result does not match this correction. Review it again.')
      if (!mounted.current) return
      setPreview(value)
      setDatePreview(value)
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  async function submit() {
    if (!preview || busy || permissionDenied) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      let request = pendingRequest.current
      if (!request) {
        const force = lifecycle
          ? lifecycle.edit.fields
              .filter((field) => lifecycle.fieldIds.includes(field.id))
              .map((field) => field.field)
          : []
        if (dateMode && (!dateRequest || !reviewedDatePlan || otherDateChanges || lifecycle))
          throw new Error('Review the balance date result before submitting this correction.')
        const values = dateMode
          ? { balance_as_of: dateRequest!.replacement_date }
          : makePatch(shown, draft, touched, preview, required, force)
        if (!Object.keys(values).length) {
          setNotice('No changes to record.')
          return
        }
        request = {
          path: lifecycle
            ? `/api/record-edits/${lifecycle.edit.id}/${lifecycle.kind}`
            : '/api/record-edits',
          body: {
            target_type: target.kind,
            target_id: target.id,
            scope,
            values,
            explanation: explanation.trim() || null,
            idempotency_key: intent.current,
            preview_token: dateMode ? datePreview!.preview_token : preview.preview_token,
            ...(dateMode ? { date_plan: dateRequest } : {}),
            ...(lifecycle
              ? { binding: lifecycle.edit.binding, field_ids: lifecycle.fieldIds }
              : {}),
          },
        }
        pendingRequest.current = request
      }
      const value = checked(
        await apiFetch<EditResult | UnchangedResult>(request.path, {
          method: 'POST',
          body: JSON.stringify(request.body),
        })
      )
      if (!mounted.current) return
      if (value.status === 'unchanged' && !('id' in value)) {
        setNotice('No changes to record.')
        pendingRequest.current = null
        return
      }
      received(value as EditResult)
      if (value.status !== 'awaiting_ack') {
        setEditing(false)
        pendingRequest.current = null
        onRecordChanged?.()
      }
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  async function newCorrection() {
    if (busy || permissionDenied) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fresh(false)
      if (!mounted.current) return
      // GET omits the original full warning request. Review a new intent;
      // never reconstruct and replay a guessed signed request.
      if (result?.status === 'awaiting_ack' && result.action_kind === 'correction') {
        setDraft((current) => ({
          ...current,
          ...Object.fromEntries(
            result.fields.map((field) => [field.field, valueText(field.proposed_value)])
          ),
        }))
        setTouched(new Set(result.fields.map((field) => field.field)))
      }
      setLifecycle(null)
      setResult(null)
      setEditing(true)
      setExplanation('')
      setSelected(new Set())
      setDecisionWarning(null)
      intent.current = crypto.randomUUID()
      pendingRequest.current = null
      onNewCorrection?.()
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  async function refreshResult() {
    if (!result || busy) return
    setBusy(true)
    setError(null)
    try {
      received(checked(await readEdit(result.id)))
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  async function begin(kind: 'amend' | 'undo') {
    if (!result || !selected.size || busy || result.date_plan) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const latest = checked(await readEdit(result.id))
      if (latest.binding !== result.binding) {
        received(latest)
        throw new Error('This edit changed. Review its fields and select again.')
      }
      const wanted = latest.fields.filter(
        (field) =>
          selected.has(field.id) && field.status === (kind === 'undo' ? 'applied' : 'pending')
      )
      if (wanted.length !== selected.size)
        throw new Error('Select only the fields to restore or amend.')
      const next = await fresh(false)
      if (!mounted.current) return
      setDraft({
        ...Object.fromEntries(
          Object.entries(next.values).map(([key, value]) => [key, valueText(value)])
        ),
        ...Object.fromEntries(
          wanted.map((field) => [
            field.field,
            valueText(kind === 'undo' ? field.baseline_value : field.proposed_value),
          ])
        ),
      })
      setTouched(new Set(wanted.map((field) => field.field)))
      setLifecycle({ kind, edit: latest, fieldIds: wanted.map((field) => field.id) })
      intent.current = crypto.randomUUID()
      pendingRequest.current = null
      setExplanation('')
      setDecisionWarning(null)
      setResult(null)
      setEditing(true)
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  async function decide(action: 'approve' | 'reject', acknowledge = false) {
    if (!result || busy || !manager) return
    setBusy(true)
    setError(null)
    try {
      const value = checked(
        await apiFetch<EditResult>(`/api/record-edits/${result.id}/${action}`, {
          method: 'POST',
          body: JSON.stringify({
            binding: result.binding,
            explanation: explanation.trim() || null,
            ...(acknowledge ? { warning_token: result.warning_token } : {}),
          }),
        })
      )
      if (!mounted.current) return
      received(value)
      setDecisionWarning(value.warning_token && action === 'reject' ? action : null)
      if (value.warning_token && action === 'approve')
        setError('A reason is required before these approved Record values can change.')
      if (!value.warning_token) onRecordChanged?.()
    } catch (error) {
      await failure(error)
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  const pending = result?.fields.filter((field) => field.status === 'pending') ?? []
  const selectedFields = result?.fields.filter((field) => selected.has(field.id)) ?? []
  if (dark) return <>{legacyFallback ?? <p>Record editing is not enabled.</p>}</>
  return (
    <section
      className="record-edit"
      aria-label={`Edit ${target.label}`}
      data-testid="record-edit-form"
      aria-busy={busy}
    >
      <h3>{target.label}</h3>
      {busy && !preview ? <p role="status">Loading the current Record…</p> : null}
      {editing && preview ? (
        <>
          <p>
            {lifecycle?.kind === 'undo'
              ? 'Review the selected fields to restore.'
              : 'Review changes to this record.'}
          </p>
          {target.kind === 'instrument' && !lifecycle && dateReferences.length > 0 ? (
            <div className="record-edit-actions">
              <button
                disabled={busy || permissionDenied}
                onClick={dateMode ? cancelDateCorrection : openDateCorrection}
              >
                {dateMode ? 'Cancel date correction' : 'Correct a recorded balance date'}
              </button>
            </div>
          ) : null}
          <div className="record-edit-grid">
            {shown
              .filter((field) => !dateMode || field.key !== 'balance_as_of')
              .map((field) => {
                const dirty = (draft[field.key] ?? '') !== valueText(preview.values[field.key])
                const kind = inputKind(field)
                const options =
                  fieldSchema(field).enum ?? (kind === 'boolean' ? ['true', 'false'] : [])
                return (
                  <div
                    className={dirty ? 'record-edit-field changed' : 'record-edit-field'}
                    key={field.key}
                  >
                    <label htmlFor={`edit-${target.id}-${field.key}`}>
                      {field.label}
                      {required.includes(field.key) ? ' · required' : ''}
                    </label>
                    <span className="record-edit-current">
                      Current: {valueText(preview.values[field.key]) || 'Not recorded'}
                    </span>
                    {kind === 'enum' || kind === 'boolean' ? (
                      <select
                        id={`edit-${target.id}-${field.key}`}
                        value={draft[field.key] ?? ''}
                        disabled={busy || permissionDenied}
                        onChange={(event) => change(field.key, event.target.value)}
                      >
                        <option value="">Not recorded</option>
                        {options.map((option) => (
                          <option key={String(option)} value={String(option)}>
                            {String(option).replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`edit-${target.id}-${field.key}`}
                        type={kind === 'date' ? 'date' : 'text'}
                        inputMode={kind === 'number' ? 'decimal' : undefined}
                        value={draft[field.key] ?? ''}
                        disabled={busy || permissionDenied}
                        onChange={(event) => change(field.key, event.target.value)}
                      />
                    )}
                  </div>
                )
              })}
          </div>
          {dateMode ? (
            <div className="record-date-result">
              <div className="record-edit-grid">
                <label className="record-edit-field">
                  Recorded balance to correct
                  <select
                    value={dateReference}
                    disabled={busy || permissionDenied}
                    onChange={(event) => changeDateSelection(event.target.value, dateAsOf)}
                  >
                    <option value="">Choose a recorded balance</option>
                    {dateReferences.map((reference) => (
                      <option key={reference.id} value={reference.id}>
                        {formatOfficerDate(reference.financial_date) ?? 'Date not recorded'} ·{' '}
                        {formatNumberString(reference.value)}{' '}
                        {reference.denomination === 'units' ? 'units' : (reference.currency ?? '')}
                        {reference.is_current ? ' · current reference' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="record-edit-field">
                  Replacement balance date
                  <input
                    type="date"
                    value={draft.balance_as_of ?? ''}
                    disabled={busy || permissionDenied}
                    onChange={(event) => change('balance_as_of', event.target.value)}
                  />
                </label>
                <label className="record-edit-field">
                  Calculate result as of
                  <input
                    type="date"
                    value={dateAsOf}
                    disabled={busy || permissionDenied}
                    onChange={(event) => changeDateSelection(dateReference, event.target.value)}
                  />
                </label>
              </div>
              <p>
                Review the selected source balance and its replacement date. Other field changes
                need a separate correction.
              </p>
              {!draft.balance_as_of ? (
                <p>This request has no financial date and will remain pending.</p>
              ) : null}
              {otherDateChanges ? (
                <p role="alert">
                  Submit or withdraw the other field changes before reviewing this date correction.
                </p>
              ) : null}
              {dateReferences
                .find((reference) => reference.id === dateReference)
                ?.pending_reasons?.map((reason) => (
                  <p key={reason}>{humanizeToken(reason)}</p>
                ))}
              <button
                disabled={busy || permissionDenied || !dateRequest || otherDateChanges}
                onClick={() => void reviewDateResult()}
              >
                Review date result
              </button>
              {reviewedDatePlan ? (
                <InstrumentDateResult
                  value={dateResultView(reviewedDatePlan)}
                  scope={scope}
                  instrumentId={target.id}
                />
              ) : null}
            </div>
          ) : null}
          <label className="record-edit-reason">
            Reason (required for changes to approved Record)
            <textarea
              aria-label="Edit explanation"
              value={explanation}
              disabled={busy}
              onChange={(event) => changeExplanation(event.target.value)}
            />
          </label>
          {lifecycle?.kind === 'amend' ? (
            <p>Withdrawing selected pending fields back to current Record needs no reason.</p>
          ) : null}
          {result?.status === 'awaiting_ack' ? (
            <p className="record-edit-warning" role="alert">
              A reason is now required. Review this correction again before submitting it.
            </p>
          ) : null}
          <button
            disabled={
              busy || permissionDenied || (dateMode && (!reviewedDatePlan || otherDateChanges))
            }
            onClick={() => void submit()}
          >
            Submit correction
          </button>
        </>
      ) : null}
      {result && !editing ? (
        <div data-testid="record-edit-result">
          <p role="status">
            {result.status === 'pending' && result.date_plan && !result.date_plan.can_apply
              ? 'Pending date review'
              : resultLabel(result.status)}
          </p>
          {result.explanation ? <p>Reason: {result.explanation}</p> : null}
          {result.status === 'awaiting_ack' ? (
            <p>A reason is now required before this correction can be submitted.</p>
          ) : result.message ? (
            <p>{result.message}</p>
          ) : null}
          {result.date_plan ? (
            <InstrumentDateResult
              value={dateResultView(result.date_plan)}
              scope={scope}
              instrumentId={target.id}
            />
          ) : null}
          {!result.current_record_available ? (
            <p>{result.current_record_unavailable_reason ?? 'Current Record unavailable.'}</p>
          ) : null}
          <table>
            <thead>
              <tr>
                <th>Select</th>
                <th>Field</th>
                <th>Current Record</th>
                <th>Requested value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {result.fields.map((field) => (
                <tr key={field.id}>
                  <td>
                    {field.application_scope !== 'selected_reference' &&
                    ['applied', 'pending'].includes(field.status) &&
                    result.current_record_available ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ${fields.find((item) => item.key === field.field)?.label ?? field.field}`}
                        checked={selected.has(field.id)}
                        disabled={busy}
                        onChange={(event) =>
                          setSelected((current) => {
                            const next = new Set(current)
                            if (event.target.checked) next.add(field.id)
                            else next.delete(field.id)
                            return next
                          })
                        }
                      />
                    ) : null}
                  </td>
                  <th>
                    {field.application_scope === 'selected_reference'
                      ? 'Selected source balance date'
                      : (fields.find((item) => item.key === field.field)?.label ??
                        field.field.replace(/_/g, ' '))}
                  </th>
                  <td>
                    {field.record_available
                      ? field.application_scope === 'selected_reference'
                        ? (formatOfficerDate(valueText(field.approved_value)) ??
                          'Date not recorded')
                        : valueText(field.approved_value) || 'Not recorded'
                      : 'Unavailable'}
                  </td>
                  <td>
                    {field.application_scope === 'selected_reference'
                      ? (formatOfficerDate(valueText(field.proposed_value)) ?? 'Date not recorded')
                      : valueText(field.proposed_value) || 'Not recorded'}
                    {field.application_scope === 'selected_reference' &&
                    field.resulting_current_value ? (
                      <div className="record-edit-current">
                        Resulting current reference:{' '}
                        {formatOfficerDate(field.resulting_current_value)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {field.binding_stale
                      ? 'Record changed · review again'
                      : field.application_scope === 'selected_reference' &&
                          field.status === 'applied'
                        ? 'Reference date recorded'
                        : resultLabel(field.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="record-edit-actions">
            <button disabled={busy} onClick={() => void refreshResult()}>
              Refresh status
            </button>
            <button
              disabled={busy || permissionDenied || !result.current_record_available}
              onClick={() => void newCorrection()}
            >
              {result.status === 'awaiting_ack' ? 'Review correction again' : 'New correction'}
            </button>
            <button
              disabled={
                busy ||
                permissionDenied ||
                !selectedFields.length ||
                !!result.date_plan ||
                selectedFields.some((field) => field.status !== 'pending')
              }
              onClick={() => void begin('amend')}
            >
              Amend selected
            </button>
            <button
              disabled={
                busy ||
                permissionDenied ||
                !selectedFields.length ||
                !!result.date_plan ||
                selectedFields.some((field) => field.status !== 'applied')
              }
              onClick={() => void begin('undo')}
            >
              Restore selected
            </button>
          </div>
          {manager && result.status === 'pending' && pending.length ? (
            <>
              <label className="record-edit-reason">
                {result.explanation?.trim()
                  ? 'Decision explanation (optional)'
                  : 'Reason required to approve this change'}
                <textarea
                  aria-label="Decision explanation"
                  value={explanation}
                  disabled={busy}
                  onChange={(event) => changeExplanation(event.target.value)}
                />
              </label>
              {decisionWarning ? (
                <div role="alert" className="record-edit-warning">
                  <p>{result.message}</p>
                  <button disabled={busy} onClick={() => void decide(decisionWarning, true)}>
                    Confirm {decisionWarning === 'approve' ? 'approval' : 'rejection'} without
                    explanation
                  </button>
                </div>
              ) : (
                <div className="record-edit-actions">
                  <button
                    disabled={
                      busy ||
                      !result.current_record_available ||
                      !!result.required_fields.length ||
                      (result.date_plan != null && !result.date_plan.can_apply) ||
                      pending.some((field) => field.binding_stale)
                    }
                    onClick={() => void decide('approve')}
                  >
                    Approve pending changes
                  </button>
                  <button disabled={busy} onClick={() => void decide('reject')}>
                    Reject pending changes
                  </button>
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {documents.length ? (
        <footer className="record-edit-documents">
          <span>Documents for this object</span>
          {documents.map((document) => (
            <DocumentLink
              key={document.document_id}
              document={document}
              context={{
                fundId: scope.fund_id ?? undefined,
                tab: 'record',
                focus: `${target.kind}:${target.id}`,
              }}
            />
          ))}
        </footer>
      ) : null}
    </section>
  )
}
